import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Trophy, Timer, Zap, Brain } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────
type Operation = "+" | "-" | "×" | "÷";

interface Level {
  id: number;
  label: string;
  labelAr: string;
  emoji: string;
  color: string;
  operations: Operation[];
  range: [number, number];
  rounds: number;
  timePerQuestion: number;
  compound?: boolean;
}

interface Question {
  text: string;
  answer: number;
  options: number[];
}

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
}

// ─── Levels ──────────────────────────────────────────────
const LEVELS: Level[] = [
  { id: 0, label: "Level 0", labelAr: "مبتدئ", emoji: "🐣", color: "from-green-400 to-emerald-500", operations: ["+", "-"], range: [1, 10], rounds: 10, timePerQuestion: 15 },
  { id: 1, label: "Level 1", labelAr: "أساسي", emoji: "🌱", color: "from-blue-400 to-blue-600", operations: ["+", "-"], range: [1, 100], rounds: 10, timePerQuestion: 12 },
  { id: 2, label: "Level 2", labelAr: "الضرب", emoji: "⚡", color: "from-yellow-400 to-amber-500", operations: ["×"], range: [1, 12], rounds: 12, timePerQuestion: 10 },
  { id: 3, label: "Level 3", labelAr: "ضرب وقسمة", emoji: "🔥", color: "from-orange-400 to-red-500", operations: ["×", "÷"], range: [2, 12], rounds: 12, timePerQuestion: 10 },
  { id: 4, label: "Level 4", labelAr: "مسائل مركبة", emoji: "🧠", color: "from-purple-400 to-violet-600", operations: ["+", "-", "×"], range: [1, 20], rounds: 12, timePerQuestion: 15, compound: true },
  { id: 5, label: "Pro", labelAr: "احترافي", emoji: "🏆", color: "from-red-500 to-rose-700", operations: ["+", "-", "×", "÷"], range: [2, 50], rounds: 15, timePerQuestion: 6 },
];

// ─── Helpers ─────────────────────────────────────────────
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const generateQuestion = (level: Level): Question => {
  const op = pickRandom(level.operations);
  let a: number, b: number, answer: number, text: string;

  if (level.compound) {
    const op1 = pickRandom(["+", "-"] as Operation[]);
    const op2 = pickRandom(["×"] as Operation[]);
    a = Math.floor(Math.random() * 10) + 1;
    b = Math.floor(Math.random() * 10) + 1;
    const c = Math.floor(Math.random() * 5) + 1;
    // a op1 (b op2 c)
    const part = b * c;
    answer = op1 === "+" ? a + part : a - part;
    text = `${a} ${op1} (${b} ${op2} ${c})`;
    if (answer < 0) return generateQuestion(level); // retry if negative
  } else {
    const [lo, hi] = level.range;
    switch (op) {
      case "+":
        a = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        b = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        answer = a + b;
        break;
      case "-":
        a = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        b = Math.floor(Math.random() * Math.min(a, hi)) + (lo > 0 ? 1 : 0);
        if (b > a) [a, b] = [b, a];
        answer = a - b;
        break;
      case "×":
        a = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        b = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        answer = a * b;
        break;
      case "÷":
        b = Math.floor(Math.random() * (hi - lo)) + 2;
        answer = Math.floor(Math.random() * (hi - lo + 1)) + lo;
        a = b * answer;
        break;
      default:
        a = 1; b = 1; answer = 2;
    }
    text = `${a} ${op} ${b}`;
  }

  // Generate wrong options
  const wrongSet = new Set<number>();
  while (wrongSet.size < 3) {
    const offset = Math.floor(Math.random() * 8) + 1;
    const wrong = Math.random() > 0.5 ? answer + offset : Math.max(0, answer - offset);
    if (wrong !== answer) wrongSet.add(wrong);
  }
  const options = [...wrongSet, answer].sort(() => Math.random() - 0.5);
  return { text, answer, options };
};

const REWARD_EMOJIS = ["🌟", "⭐", "🎯", "🏅", "💎", "🚀", "🎪", "🎠"];

// ─── Component ───────────────────────────────────────────
const MentalMathGame = ({ onComplete }: Props) => {
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [finished, setFinished] = useState(false);
  const [streak, setStreak] = useState(0);
  const [showReward, setShowReward] = useState("");
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const questionTimesRef = useRef<number[]>([]);

  const startLevel = useCallback((level: Level) => {
    const qs: Question[] = [];
    for (let i = 0; i < level.rounds; i++) qs.push(generateQuestion(level));
    setQuestions(qs);
    setSelectedLevel(level);
    setCurrentIdx(0);
    setScore(0);
    setAnswered(null);
    setTimeLeft(level.timePerQuestion);
    setTotalTime(0);
    setFinished(false);
    setStreak(0);
    questionTimesRef.current = [];
  }, []);

  // Timer
  useEffect(() => {
    if (!selectedLevel || finished || answered !== null) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - count as wrong
          clearInterval(timerRef.current);
          setAnswered(-1);
          setStreak(0);
          questionTimesRef.current.push(selectedLevel.timePerQuestion);
          return 0;
        }
        return prev - 1;
      });
      setTotalTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [selectedLevel, currentIdx, finished, answered]);

  const handleAnswer = (option: number) => {
    if (answered !== null || !selectedLevel) return;
    clearInterval(timerRef.current);
    const timeUsed = selectedLevel.timePerQuestion - timeLeft;
    questionTimesRef.current.push(timeUsed);
    setAnswered(option);

    if (option === questions[currentIdx].answer) {
      // Bonus points for speed
      const speedBonus = timeLeft > selectedLevel.timePerQuestion * 0.7 ? 2 : timeLeft > selectedLevel.timePerQuestion * 0.4 ? 1 : 0;
      setScore(prev => prev + 1 + speedBonus);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak % 3 === 0) setShowReward(pickRandom(REWARD_EMOJIS));
        return newStreak;
      });
    } else {
      setStreak(0);
    }
  };

  // Auto-advance
  useEffect(() => {
    if (answered === null || !selectedLevel) return;
    const timer = setTimeout(() => {
      if (currentIdx < questions.length - 1) {
        setCurrentIdx(prev => prev + 1);
        setAnswered(null);
        setTimeLeft(selectedLevel.timePerQuestion);
      } else {
        setFinished(true);
        const maxScore = questions.length * 3; // max with speed bonus
        const stars = score >= maxScore * 0.8 ? 3 : score >= maxScore * 0.5 ? 2 : score > 0 ? 1 : 0;
        onComplete?.({ score, maxScore, stars });
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [answered]);

  // Clear reward
  useEffect(() => {
    if (!showReward) return;
    const t = setTimeout(() => setShowReward(""), 1500);
    return () => clearTimeout(t);
  }, [showReward]);

  // ─── Level Selection ───
  if (!selectedLevel) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <p className="text-5xl">🧮</p>
          <h2 className="text-2xl font-bold text-foreground">الحساب الذهني</h2>
          <p className="text-muted-foreground text-sm">Boothy Math - اختر المستوى وابدأ التحدي!</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {LEVELS.map((level, i) => (
            <motion.div
              key={level.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Card
                className="cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary/30 transition-all shadow-card hover:shadow-elevated"
                onClick={() => startLevel(level)}
              >
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-br ${level.color} p-5 text-center text-white`}>
                    <p className="text-4xl mb-2">{level.emoji}</p>
                    <h3 className="text-lg font-bold">{level.label}</h3>
                    <p className="text-sm font-medium opacity-90">{level.labelAr}</p>
                    <div className="flex items-center justify-center gap-2 mt-2 text-xs opacity-80">
                      <span>{level.rounds} سؤال</span>
                      <span>•</span>
                      <span>{level.timePerQuestion}ث</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs opacity-70">
                      {level.operations.map(op => (
                        <span key={op} className="bg-white/20 px-1.5 py-0.5 rounded">{op}</span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Results ───
  if (finished) {
    const maxScore = questions.length * 3;
    const percentage = Math.round((score / maxScore) * 100);
    const stars = percentage >= 80 ? 3 : percentage >= 50 ? 2 : percentage > 0 ? 1 : 0;
    const avgTime = questionTimesRef.current.length > 0
      ? (questionTimesRef.current.reduce((a, b) => a + b, 0) / questionTimesRef.current.length).toFixed(1)
      : "0";
    const correctCount = questionTimesRef.current.filter((_, i) => {
      // approximate: we stored times but need correct count from score
      return true;
    }).length;

    return (
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
        <Card className="max-w-md mx-auto overflow-hidden shadow-elevated">
          <div className={`bg-gradient-to-br ${selectedLevel.color} p-8 text-center text-white`}>
            <Trophy className="w-16 h-16 mx-auto mb-3 drop-shadow-lg" />
            <h2 className="text-2xl font-bold mb-2">تم الانتهاء! 🎉</h2>
            <p className="opacity-90">{selectedLevel.emoji} {selectedLevel.labelAr}</p>
          </div>
          <CardContent className="p-6 space-y-4 text-center">
            <div className="flex justify-center gap-1">
              {[1, 2, 3].map(s => (
                <Star key={s} className={cn("w-10 h-10", s <= stars ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/20")} />
              ))}
            </div>
            <div className="text-4xl font-bold text-primary">{percentage}%</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-muted/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-foreground">{score}</p>
                <p className="text-muted-foreground text-xs">النقاط</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-foreground">{questions.length}</p>
                <p className="text-muted-foreground text-xs">الأسئلة</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-foreground">{avgTime}ث</p>
                <p className="text-muted-foreground text-xs">متوسط الوقت</p>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => startLevel(selectedLevel)} className="flex-1 gap-2">
                <RotateCcw className="w-4 h-4" /> أعد المحاولة
              </Button>
              <Button variant="outline" onClick={() => setSelectedLevel(null)} className="flex-1">
                المستويات
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ─── Game Play ───
  const q = questions[currentIdx];
  const progress = ((currentIdx) / questions.length) * 100;
  const timerPercent = (timeLeft / selectedLevel.timePerQuestion) * 100;
  const isCorrect = answered !== null && answered === q.answer;
  const isWrong = answered !== null && answered !== q.answer;

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Streak reward */}
      <AnimatePresence>
        {showReward && (
          <motion.div
            initial={{ scale: 0, y: 0 }}
            animate={{ scale: 1.5, y: -40 }}
            exit={{ opacity: 0, scale: 0 }}
            className="fixed top-1/3 left-1/2 -translate-x-1/2 z-50 text-7xl pointer-events-none"
          >
            {showReward}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-sm gap-1">
          {selectedLevel.emoji} {selectedLevel.labelAr}
        </Badge>
        <Badge variant="outline" className="text-sm">
          {currentIdx + 1} / {questions.length}
        </Badge>
      </div>

      {/* Progress */}
      <Progress value={progress} className="h-2" />

      {/* Stats row */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-muted-foreground">
          <Zap className="w-4 h-4 text-amber-500" />
          <span>{score} نقطة</span>
        </div>
        {streak >= 2 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-1 text-orange-500 font-bold"
          >
            🔥 {streak}x
          </motion.div>
        )}
        <div className={cn(
          "flex items-center gap-1 font-bold",
          timeLeft <= 3 ? "text-red-500 animate-pulse" : "text-muted-foreground"
        )}>
          <Timer className="w-4 h-4" />
          <span>{timeLeft}ث</span>
        </div>
      </div>

      {/* Timer bar */}
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <motion.div
          className={cn(
            "h-full rounded-full transition-colors",
            timerPercent > 50 ? "bg-green-500" : timerPercent > 25 ? "bg-yellow-500" : "bg-red-500"
          )}
          animate={{ width: `${timerPercent}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIdx}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
        >
          <Card className={cn(
            "shadow-elevated overflow-hidden transition-all",
            isCorrect && "ring-4 ring-green-400",
            isWrong && "ring-4 ring-red-400"
          )}>
            <div className={`bg-gradient-to-br ${selectedLevel.color} p-8 text-center`}>
              <Brain className="w-8 h-8 text-white/40 mx-auto mb-2" />
              <p className="text-4xl sm:text-5xl font-bold text-white tracking-wider">
                {q.text} = ?
              </p>
            </div>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {q.options.map((opt, i) => {
                  const isThis = answered === opt;
                  const isRight = opt === q.answer;
                  let bg = "";
                  if (answered !== null) {
                    if (isRight) bg = "bg-green-500 text-white border-green-500 scale-105";
                    else if (isThis && !isRight) bg = "bg-red-500 text-white border-red-500 shake";
                  }
                  return (
                    <motion.div
                      key={i}
                      whileHover={answered === null ? { scale: 1.05 } : undefined}
                      whileTap={answered === null ? { scale: 0.95 } : undefined}
                    >
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-16 text-2xl font-bold transition-all",
                          bg,
                          answered === null && "hover:bg-primary/10 hover:border-primary"
                        )}
                        onClick={() => handleAnswer(opt)}
                        disabled={answered !== null}
                      >
                        {opt}
                        {answered !== null && isRight && " ✅"}
                        {answered !== null && isThis && !isRight && " ❌"}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MentalMathGame;
